/**
 * API utility functions for the Feedback Labeling feature
 *
 * All requests use the existing /submit-feedback endpoint with different action values.
 * This maintains backward compatibility with the existing feedback submission system.
 */

import type {
  AuthResponse,
  GetFeedbackResponse,
  SubmitLabelResponse,
  RecommendationSnapshot
} from '@/types/labeling';

// Get API endpoint from environment
const API_BASE = import.meta.env.VITE_API_ENDPOINT || '/api';
const ENDPOINT = `${API_BASE}/submit-feedback`;

/**
 * Authenticate a labeler with email and password
 * Returns JWT token and user information on success
 *
 * @param email - Labeler's email address
 * @param password - Labeler's password
 * @returns Promise with authentication response
 * @throws Error if authentication fails
 */
export const authenticate = async (
  email: string,
  password: string
): Promise<AuthResponse> => {
  try {
    const res = await fetch(ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        action: 'authenticate',
        email,
        password
      })
    });

    if (!res.ok) {
      const errorData = await res.json().catch(() => ({ error: 'Authentication failed' }));
      throw new Error(errorData.error || `Authentication failed with status ${res.status}`);
    }

    const data: AuthResponse = await res.json();
    return data;
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('Authentication failed. Please try again.');
  }
};

/**
 * Fetch unlabeled feedback for the authenticated user
 * Returns only feedback items not yet labeled by the current user
 *
 * @param token - JWT authentication token
 * @returns Promise with feedback array and count
 * @throws Error if request fails
 */
export const getUnlabeledFeedback = async (
  token: string
): Promise<GetFeedbackResponse> => {
  try {
    const res = await fetch(ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        action: 'getFeedback',
        token
      })
    });

    if (!res.ok) {
      const errorData = await res.json().catch(() => ({ error: 'Failed to fetch feedback' }));

      // Handle token expiration
      if (res.status === 401) {
        throw new Error('Your session has expired. Please log in again.');
      }

      throw new Error(errorData.error || `Failed to fetch feedback with status ${res.status}`);
    }

    const data: GetFeedbackResponse = await res.json();
    return data;
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('Failed to fetch feedback. Please try again.');
  }
};

/**
 * Submit a label for a feedback item
 * Writes the label to the Google Sheets Labels tab with dynamic fields
 *
 * @param token - JWT authentication token
 * @param labelData - Label information with dynamic fields and optional Sanity snapshot
 * @returns Promise with success response
 * @throws Error if submission fails
 */
export const submitLabel = async (
  token: string,
  labelData: {
    feedbackId: number;       // Row number from Feedback tab - written to Labels Column A
    recommendationId?: string; // Optional UUID for Sanity lookup - kept for logging/reference
    fields: Record<string, any>;
    sanitySnapshot?: RecommendationSnapshot;
  }
): Promise<SubmitLabelResponse> => {
  try {
    const res = await fetch(ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        action: 'submitLabel',
        token,
        labelData
      })
    });

    if (!res.ok) {
      const errorData = await res.json().catch(() => ({ error: 'Failed to submit label' }));

      // Handle token expiration
      if (res.status === 401) {
        throw new Error('Your session has expired. Please log in again.');
      }

      // Handle validation errors
      if (res.status === 400) {
        throw new Error(errorData.message || errorData.error || 'Invalid label data');
      }

      throw new Error(errorData.error || `Failed to submit label with status ${res.status}`);
    }

    const data: SubmitLabelResponse = await res.json();
    return data;
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('Failed to submit label. Please try again.');
  }
};
